import { ReactElement } from 'react'

import { QuestionCircleOutlined } from '@ant-design/icons'
import { TFunction } from 'i18next'
import isArray from 'lodash/isArray'
import isUndefined from 'lodash/isUndefined'
import { Translation } from 'react-i18next'

import { ToolTip } from 'components/ToolTip'
import { date, type DateUnit, type Dayjs } from 'configuration/dayjs'
import { monthBucketRange, weekBucketRange } from 'const/dateBuckets'
import {
    Aggregation,
    DateRangeFilter,
    PeriodDeltaType,
    PresetRange,
    PresetRangeMap,
} from 'types'
import { DateGrain } from 'types/dataSources'

import { formatCerebroDate, formatJSONDateTime } from './formatting'
import { isPresetDateRange } from './typeGuard'

/**
 * Category Performance lag hours - calculated as 3 weeks from the start of the current week
 * This ensures data alignment with backend aggregation windows for category performance
 */
export const PERFORMANCE_LAG_HOURS = date().diff(
    date().startOf('week').subtract(3, 'weeks'),
    'hours'
)

function daysAgo(days: number, fromDate?: string | Dayjs): Dayjs {
    return date(fromDate).subtract(days, 'days').startOf('day')
}

export function dayRange(
    days: number,
    fromDate?: string | Dayjs,
    includeFromDay?: boolean,
    withLag?: boolean
): [Dayjs, Dayjs] {
    return [
        daysAgo(withLag ? days - 1 : days, fromDate),
        includeFromDay ? date(fromDate).startOf('day') : daysAgo(1, fromDate),
    ]
}

function monthRange(months: number, fromDate?: string | Dayjs): [Dayjs, Dayjs] {
    return [
        date(fromDate)
            .subtract(months, 'months')
            .startOf('month')
            .startOf('day'),
        date(fromDate).subtract(1, 'month').endOf('month').startOf('day'),
    ]
}

function calendarRange(
    amount: number,
    unit: DateUnit,
    fromDate?: string | Dayjs
): [Dayjs, Dayjs] {
    return [
        date(fromDate).subtract(amount, unit).startOf(unit).startOf('day'),
        date(fromDate)
            .subtract(Math.min(amount, 1), unit)
            .endOf(unit)
            .startOf('day'),
    ]
}

function resolveFiscalYearStartMonth(fiscalYearStartMonth?: number): number {
    return fiscalYearStartMonth ?? 1
}

function getCurrentFiscalYearStart(
    referenceDate: Dayjs,
    startMonth: number
): Dayjs {
    const ref = referenceDate.startOf('day')
    const calendarMonth = ref.month() + 1
    const year = ref.year()

    if (calendarMonth >= startMonth) {
        return date({ year, month: startMonth - 1, day: 1 }).startOf('day')
    }

    return date({ year: year - 1, month: startMonth - 1, day: 1 }).startOf(
        'day'
    )
}

function getFiscalYearToDateRange(
    startingPoint: Dayjs,
    startMonth: number
): [Dayjs, Dayjs] {
    return [
        getCurrentFiscalYearStart(startingPoint, startMonth),
        startingPoint.startOf('day'),
    ]
}

function getLastCompletedFiscalYearRange(
    startingPoint: Dayjs,
    startMonth: number
): [Dayjs, Dayjs] {
    const currentFiscalYearStart = getCurrentFiscalYearStart(
        startingPoint,
        startMonth
    )
    const lastFiscalYearEnd = currentFiscalYearStart

        .subtract(1, 'day')
        .startOf('day')

    return [
        getCurrentFiscalYearStart(lastFiscalYearEnd, startMonth),
        lastFiscalYearEnd,
    ]
}

export const getName = (
    name: ReactElement,
    subTitle?: string
): ReactElement => {
    return (
        <>
            {name}
            {subTitle && (
                <span
                    style={{
                        display: 'block',
                        fontStyle: 'italic',
                        fontWeight: 500,
                        fontSize: '13px',
                        color: 'rgba(121, 124, 126, 0.60)',
                        marginTop: '-2px',
                    }}
                >
                    {subTitle}
                </span>
            )}
        </>
    )
}

const isLagInHour = (lagInHours: number | undefined | []): boolean => {
    if (
        lagInHours === undefined ||
        (isArray(lagInHours) && lagInHours.length === 0) ||
        lagInHours === 0
    ) {
        return false
    }
    return true
}

export const presetRanges = (
    fromDate?: string | Dayjs,
    lagInHours?: number,
    fiscalYearStartMonth?: number
): PresetRangeMap => {
    const todayWithLag = date(fromDate)
        .subtract(lagInHours ?? 0, 'hours')
        .startOf('day')
    const today = date().startOf('day')
    const startingPoint = todayWithLag.isAfter(today) ? today : todayWithLag
    const fiscalStartMonth = resolveFiscalYearStartMonth(fiscalYearStartMonth)

    return {
        today: {
            name: getName(
                <>
                    <Translation>
                        {(t) =>
                            t(
                                'filters:DateRangeFilter.presetRanges.today',
                                'Today'
                            )
                        }
                    </Translation>
                    <ToolTip
                        title="Today may contain incomplete data"
                        placement="bottom"
                    >
                        <QuestionCircleOutlined style={{ marginLeft: '5px' }} />
                    </ToolTip>
                </>
            ),
            range: [
                date(startingPoint).startOf('day'),
                date(startingPoint).endOf('day'),
            ],
        },
        yesterday: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.yesterday',
                            'Yesterday'
                        )
                    }
                </Translation>
            ),
            range: dayRange(1, startingPoint),
            isRecursive: true,
        },
        this_week: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.thisWeek',
                            'This Week'
                        )
                    }
                </Translation>
            ),
            range: calendarRange(0, 'weeks', startingPoint),
        },
        last_7_days: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last7Days',
                            'Last 7 Days'
                        )
                    }
                </Translation>
            ),
            shortName: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last7DaysShort',
                            '7D'
                        )
                    }
                </Translation>
            ),
            range: dayRange(
                7,
                startingPoint,
                isLagInHour(lagInHours),
                isLagInHour(lagInHours)
            ),
            isRecursive: true,
        },
        last_week: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.lastWeek',
                            'Last Week'
                        )
                    }
                </Translation>
            ),
            range: calendarRange(1, 'week', startingPoint),
            isRecursive: true,
        },
        last_2_weeks: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last2Weeks',
                            'Last 2 Weeks'
                        )
                    }
                </Translation>
            ),
            range: calendarRange(2, 'weeks', startingPoint),
            isRecursive: true,
        },
        last_14_days: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last14Days',
                            'Last 14 Days'
                        )
                    }
                </Translation>
            ),
            shortName: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last14DaysShort',
                            '14D'
                        )
                    }
                </Translation>
            ),
            range: dayRange(
                14,
                startingPoint,
                isLagInHour(lagInHours),
                isLagInHour(lagInHours)
            ),
            isRecursive: true,
        },
        this_month: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.thisMonth',
                            'This Month'
                        )
                    }
                </Translation>
            ),
            range: calendarRange(0, 'months', startingPoint),
        },
        last_28_days: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last28Days',
                            'Last 28 Days'
                        )
                    }
                </Translation>
            ),
            range: dayRange(
                28,
                startingPoint,
                isLagInHour(lagInHours),
                isLagInHour(lagInHours)
            ),
            isRecursive: true,
        },
        last_30_days: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last30Days',
                            'Last 30 Days'
                        )
                    }
                </Translation>
            ),
            shortName: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last30DaysShort',
                            '30D'
                        )
                    }
                </Translation>
            ),
            range: dayRange(
                30,
                startingPoint,
                isLagInHour(lagInHours),
                isLagInHour(lagInHours)
            ),
            isRecursive: true,
        },
        last_month: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.lastMonth',
                            'Last Month'
                        )
                    }
                </Translation>
            ),
            range: calendarRange(1, 'month', startingPoint),
            isRecursive: true,
        },
        last_60_days: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last60Days',
                            'Last 60 Days'
                        )
                    }
                </Translation>
            ),
            range: dayRange(
                60,
                startingPoint,
                isLagInHour(lagInHours),
                isLagInHour(lagInHours)
            ),
            isRecursive: true,
        },
        last_8_weeks: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last8Weeks',
                            'Last 8 Weeks'
                        )
                    }
                </Translation>
            ),
            shortName: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last8WeeksShort',
                            '8W'
                        )
                    }
                </Translation>
            ),
            range: weekBucketRange(8, startingPoint),
            isRecursive: true,
        },
        this_quarter: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.thisQuarter',
                            'This Quarter'
                        )
                    }
                </Translation>
            ),
            range: calendarRange(0, 'quarters', startingPoint),
        },
        last_90_days: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last90Days',
                            'Last 90 Days'
                        )
                    }
                </Translation>
            ),
            shortName: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last90DaysShort',
                            '90D'
                        )
                    }
                </Translation>
            ),
            range: dayRange(
                90,
                startingPoint,
                isLagInHour(lagInHours),
                isLagInHour(lagInHours)
            ),
            isRecursive: true,
        },
        last_12_weeks: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last12Weeks',
                            'Last 12 Weeks'
                        )
                    }
                </Translation>
            ),
            shortName: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last12WeeksShort',
                            '12W'
                        )
                    }
                </Translation>
            ),
            range: weekBucketRange(12, startingPoint),
            isRecursive: true,
        },
        last_6_complete_months: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last6CompleteMonths',
                            'Last 6 Months'
                        )
                    }
                </Translation>
            ),
            shortName: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last6CompleteMonthsShort',
                            '6M'
                        )
                    }
                </Translation>
            ),
            range: monthBucketRange(6, startingPoint),
            isRecursive: true,
        },
        last_12_complete_months: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last12CompleteMonths',
                            'Last 12 Months'
                        )
                    }
                </Translation>
            ),
            shortName: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last12CompleteMonthsShort',
                            '12M'
                        )
                    }
                </Translation>
            ),
            range: monthBucketRange(12, startingPoint),
            isRecursive: true,
        },
        last_3_months: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last3Months',
                            'Last 3 Months'
                        )
                    }
                </Translation>
            ),
            range: monthRange(3, startingPoint),
            isRecursive: true,
        },
        last_quarter: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.lastQuarter',
                            'Last Quarter'
                        )
                    }
                </Translation>
            ),
            range: calendarRange(1, 'quarter', startingPoint),
            isRecursive: true,
        },
        last_180_days: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last180Days',
                            'Last 180 Days'
                        )
                    }
                </Translation>
            ),
            range: dayRange(
                180,
                startingPoint,
                isLagInHour(lagInHours),
                isLagInHour(lagInHours)
            ),
            isRecursive: true,
        },
        last_6_months: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last6Months',
                            'Last 6 Months'
                        )
                    }
                </Translation>
            ),
            range: monthRange(6, startingPoint),
            isRecursive: true,
        },
        this_year: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.thisYear',
                            '{{year}}',
                            {
                                year: startingPoint.year(),
                            }
                        )
                    }
                </Translation>
            ),
            range: calendarRange(0, 'years', startingPoint),
        },
        last_365_days: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last365Days',
                            'Last 365 Days'
                        )
                    }
                </Translation>
            ),
            range: dayRange(
                365,
                startingPoint,
                isLagInHour(lagInHours),
                isLagInHour(lagInHours)
            ),
            isRecursive: true,
        },
        last_730_days: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last730Days',
                            'Last 2 Years'
                        )
                    }
                </Translation>
            ),
            range: dayRange(
                730,
                startingPoint,
                isLagInHour(lagInHours),
                isLagInHour(lagInHours)
            ),
            isRecursive: true,
        },
        last_12_months: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last12Months',
                            'Last 12 Months'
                        )
                    }
                </Translation>
            ),
            range: monthRange(12, startingPoint),
            isRecursive: true,
        },
        last_year: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.previousFullYear',
                            '{{year}}',
                            {
                                year: date(startingPoint)
                                    .subtract(1, 'year')
                                    .year(),
                            }
                        )
                    }
                </Translation>
            ),
            range: calendarRange(1, 'year', fromDate),
            isRecursive: true,
        },
        last_2_years: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.previousFull2Years',
                            '{{year1}}–{{year2}}',
                            {
                                year1: date(startingPoint)
                                    .subtract(2, 'years')
                                    .year(),
                                year2: date(startingPoint)
                                    .subtract(1, 'year')
                                    .year(),
                            }
                        )
                    }
                </Translation>
            ),
            range: calendarRange(2, 'years', startingPoint),
            isRecursive: true,
        },
        last_7_days_to_date: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last7DaysToDate',
                            'Last 7 Days | Today Included'
                        )
                    }
                </Translation>
            ),
            range: dayRange(7, startingPoint, true),
        },
        last_14_days_to_date: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last14DaysToDate',
                            'Last 14 Days | Today Included'
                        )
                    }
                </Translation>
            ),
            range: dayRange(14, startingPoint, true),
        },
        last_28_days_to_date: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last28DaysToDate',
                            'Last 28 Days | Today Included'
                        )
                    }
                </Translation>
            ),
            range: dayRange(28, startingPoint, true),
        },
        last_30_days_to_date: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last30DaysToDate',
                            'Last 30 Days | Today Included'
                        )
                    }
                </Translation>
            ),
            range: dayRange(30, startingPoint, true),
        },
        last_90_days_to_date: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last90DaysToDate',
                            'Last 90 Days | Today Included'
                        )
                    }
                </Translation>
            ),
            range: dayRange(90, startingPoint, true),
        },
        last_180_days_to_date: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.last180DaysToDate',
                            'Last 180 Days | Today Included'
                        )
                    }
                </Translation>
            ),
            range: dayRange(180, startingPoint, true),
        },
        month_to_date: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.monthToDate',
                            'Month-to-Date'
                        )
                    }
                </Translation>
            ),
            shortName: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.monthToDateShort',
                            'MTD'
                        )
                    }
                </Translation>
            ),
            range: [
                date(startingPoint).startOf('month'),
                date(startingPoint).startOf('day'),
            ],
            priorPeriodUnit: 'month',
        },
        quarter_to_date: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.quarterToDate',
                            'Quarter-to-Date'
                        )
                    }
                </Translation>
            ),
            shortName: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.quarterToDateShort',
                            'QTD'
                        )
                    }
                </Translation>
            ),
            range: [
                date(startingPoint).startOf('quarter'),
                date(startingPoint).startOf('day'),
            ],
            priorPeriodUnit: 'quarter',
        },
        year_to_date: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.yearToDate',
                            'Year-to-Date'
                        )
                    }
                </Translation>
            ),
            shortName: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.yearToDateShort',
                            'YTD'
                        )
                    }
                </Translation>
            ),
            range: [
                date(startingPoint).startOf('year'),
                date(startingPoint).startOf('day'),
            ],
            priorPeriodUnit: 'year',
        },
        fiscal_year_to_date: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.fiscalYearToDate',
                            'Fiscal Year to Date'
                        )
                    }
                </Translation>
            ),
            shortName: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.fiscalYearToDateShort',
                            'FYTD'
                        )
                    }
                </Translation>
            ),
            range: getFiscalYearToDateRange(startingPoint, fiscalStartMonth),
            priorPeriodUnit: 'year',
        },
        last_fiscal_year: {
            name: getName(
                <Translation>
                    {(t) =>
                        t(
                            'filters:DateRangeFilter.presetRanges.lastFiscalYear',
                            'Last Fiscal Year'
                        )
                    }
                </Translation>
            ),
            range: getLastCompletedFiscalYearRange(
                startingPoint,
                fiscalStartMonth
            ),
            isRecursive: true,
        },
    }
}

function parallelPriorRange(
    dateRangeFilter: PresetRange,
    currentRangeStart: Dayjs,
    currentRangeEnd: Dayjs,
    hasHourlyData = false,
    fiscalYearStartMonth?: number
): string[] {
    const presetRange = presetRanges(
        undefined,
        undefined,
        fiscalYearStartMonth
    )[dateRangeFilter]
    const fromDate = presetRange.priorPeriodUnit
        ? currentRangeEnd
        : currentRangeStart
    const unit = presetRange.priorPeriodUnit ?? 'day'
    const lastPeriodEnd = presetRange.isRecursive
        ? fromDate
        : fromDate.subtract(1, unit)
    const [min, max] = presetRanges(
        lastPeriodEnd.format('YYYY-MM-DD'),
        undefined,
        fiscalYearStartMonth
    )[dateRangeFilter].range
    const formatter = hasHourlyData ? formatJSONDateTime : formatCerebroDate
    return [formatter(min), formatter(max)]
}

export function isParallelPriorPeriod(
    dateRangeFilter: DateRangeFilter
): boolean {
    return (
        isPresetDateRange(dateRangeFilter) &&
        Boolean(presetRanges()[dateRangeFilter].priorPeriodUnit)
    )
}

function getDateRange(
    dateRange: DateRangeFilter | Dayjs[] | undefined,
    format: string,
    fromDate?: string | Dayjs,
    lagInHours?: number,
    fiscalYearStartMonth?: number
): string[] | null {
    if (isUndefined(dateRange)) {
        return null
    }

    let range: string[]

    if (isArray(dateRange)) {
        range = dateRange
            .map((value: string | Dayjs) =>
                date.isDayjs(value) ? value : date(value, 'YYYY-MM-DD')
            )
            .map((day: Dayjs) => day.format(format))

        return range ? range : null
    }

    const presetRange = presetRanges(
        fromDate,
        lagInHours,
        fiscalYearStartMonth
    )[dateRange]
    range = presetRange.range.map((day) => day.format(format))

    return range
}

export function getRenderedDateRange(
    dateRange: DateRangeFilter | Dayjs[] | undefined,
    format: string,
    fromDate?: string | Dayjs,
    withoutPreset?: boolean,
    lagInHours?: number,
    fiscalYearStartMonth?: number
): string | null | ReactElement {
    if (!dateRange) {
        return null
    }
    const dates = getDateRange(
        dateRange,
        format,
        fromDate,
        lagInHours,
        fiscalYearStartMonth
    )
    if (!dates) {
        return null
    }

    if (isArray(dateRange)) {
        return dates ? `${dates[0]} – ${dates[1]}` : null
    }

    const presetRange = presetRanges(
        fromDate,
        lagInHours,
        fiscalYearStartMonth
    )[dateRange]

    return (
        <>
            {withoutPreset ? '' : presetRange.name} ({dates[0]} – {dates[1]})
        </>
    )
}

export function getDateRangeArray(
    dateRange: DateRangeFilter,
    hasHourlyData?: boolean,
    rangeLag?: number,
    fiscalYearStartMonth?: number
): Dayjs[] {
    const range = isArray(dateRange)
        ? dateRange.map((value: string | Dayjs) =>
              date.isDayjs(value) ? value : date(value)
          )
        : presetRanges(date(), rangeLag, fiscalYearStartMonth)[dateRange].range

    return hasHourlyData
        ? [range[0].startOf('day'), range[1].endOf('day')]
        : range
}

export function getPriorPeriodDateRange(
    min: string | Dayjs,
    max: string | Dayjs,
    periodDeltaType: PeriodDeltaType | undefined,
    dateRangeFilter: DateRangeFilter,
    hasHourlyData = false,
    fiscalYearStartMonth?: number
): string[] {
    const minDate = date(min).startOf('day')
    const maxTmp = date(max)
    const maxDate = hasHourlyData ? maxTmp.endOf('day') : maxTmp.startOf('day')

    if (periodDeltaType === 'prior_year') {
        return [
            formatCerebroDate(minDate.subtract(1, 'year')),
            formatCerebroDate(maxDate.subtract(1, 'year')),
        ]
    }

    if (
        periodDeltaType === 'prior_period' &&
        isPresetDateRange(dateRangeFilter)
    ) {
        return parallelPriorRange(
            dateRangeFilter,
            minDate,
            maxDate,
            hasHourlyData,
            fiscalYearStartMonth
        )
    }

    // prior period
    const diffInDays = maxDate.diff(minDate, 'days') + 1
    const formatter = hasHourlyData ? formatJSONDateTime : formatCerebroDate
    return [
        formatter(minDate.subtract(diffInDays, 'days')),
        formatter(maxDate.subtract(diffInDays, 'days')),
    ]
}

export function getDateRangeValues(
    dateRange: DateRangeFilter | Dayjs[],
    fromDate?: string | Dayjs,
    lagInHours?: number,
    fiscalYearStartMonth?: number
): string[] | null {
    const range: Dayjs[] =
        isArray(dateRange) && date.isDayjs(dateRange[0])
            ? (dateRange as Dayjs[])
            : getDateRangeArray(
                  dateRange as DateRangeFilter,
                  false,
                  undefined,
                  fiscalYearStartMonth
              )

    const year1 = range[0].year()
    const year2 = range[1].year()
    const currentYear = date().year()

    const format = year1 === year2 && year1 === currentYear ? 'M/D' : 'M/D/YY'

    return getDateRange(
        dateRange,
        format,
        fromDate,
        lagInHours,
        fiscalYearStartMonth
    )
}

export function getFormattedDateRange(
    dateRange: DateRangeFilter | Dayjs[],
    fromDate?: string | Dayjs,
    withoutPreset?: boolean,
    lagInHours?: number,
    fiscalYearStartMonth?: number
): string | null | ReactElement {
    const range: Dayjs[] =
        isArray(dateRange) && date.isDayjs(dateRange[0])
            ? (dateRange as Dayjs[])
            : getDateRangeArray(
                  dateRange as DateRangeFilter,
                  false,
                  undefined,
                  fiscalYearStartMonth
              )

    const year1 = range[0].year()
    const year2 = range[1].year()
    const currentYear = date().year()

    const format = year1 === year2 && year1 === currentYear ? 'M/D' : 'M/D/YY'

    return getRenderedDateRange(
        dateRange,
        format,
        fromDate,
        withoutPreset,
        lagInHours,
        fiscalYearStartMonth
    )
}

export const getDateRangeInDays = (
    rangeStart: string | Dayjs,
    rangeEnd: string | Dayjs
): number => {
    const startDate = date.isDayjs(rangeStart) ? rangeStart : date(rangeStart)
    const endDate = date.isDayjs(rangeEnd) ? rangeEnd : date(rangeEnd)

    return endDate.diff(startDate, 'days')
}

/**
 * The window a query actually covered, as reported by the server.
 *
 * A weekly data source widens the requested range to whole weeks, so the range
 * the user picked and the range the numbers cover are two different things. This
 * is the second one — never recomputed on the client, so a chip can't disagree
 * with the data beside it.
 */
export interface ResolvedDateWindow {
    start: string
    end: string
    grain?: DateGrain
}

/**
 * Whole grain units in a window, or null when it doesn't divide evenly (which
 * would mean the server aligned to something other than the declared grain).
 */
export function countWholeGrainUnits(
    window: ResolvedDateWindow
): number | null {
    if (window.grain?.unit !== 'week') {
        return null
    }
    const days = getDateRangeInDays(window.start, window.end) + 1
    return days > 0 && days % 7 === 0 ? days / 7 : null
}

/**
 * Whether a window covers whole Sun–Sat weeks.
 *
 * Both endpoints, not the span: a 14-day window starting on a Wednesday divides
 * evenly by 7 and is still two half weeks.
 */
export function isWholeWeekWindow(startISO: string, endISO: string): boolean {
    return (
        date(startISO, 'YYYY-MM-DD').day() === 0 &&
        date(endISO, 'YYYY-MM-DD').day() === 6
    )
}

/** `8/1` — one date, formatted the way {@link formatCompactDateRange} formats two. */
export function formatCompactDate(iso: string): string {
    const parsed = date(iso, 'YYYY-MM-DD')
    const [formatted] = getDateRangeValues([parsed, parsed]) ?? [iso]

    return formatted
}

/** `6/28 – 8/1`, formatted the way the filter chips already format a range. */
export function formatCompactDateRange(
    startISO: string,
    endISO: string
): string {
    const [start, end] = getDateRangeValues([
        date(startISO, 'YYYY-MM-DD'),
        date(endISO, 'YYYY-MM-DD'),
    ]) ?? [startISO, endISO]

    return `${start} – ${end}`
}

/**
 * `Last Month · 6/28 – 8/1 (5 weeks)` — the preset the user picked, followed by
 * the window that answered it. Renders the resolved dates only; the requested
 * ones go in the chip's hint, where there is room to say they were widened.
 */
export function getResolvedDateRangeLabel(
    dateRange: DateRangeFilter | Dayjs[] | undefined,
    resolved: ResolvedDateWindow,
    fromDate?: string | Dayjs,
    lagInHours?: number,
    fiscalYearStartMonth?: number
): ReactElement {
    const dates = formatCompactDateRange(resolved.start, resolved.end)

    const presetName =
        dateRange && !isArray(dateRange)
            ? presetRanges(fromDate, lagInHours, fiscalYearStartMonth)[
                  dateRange
              ]?.name
            : undefined

    const weeks = countWholeGrainUnits(resolved)

    return (
        <>
            {presetName ? <>{presetName} · </> : ''}
            {dates}
            {weeks ? (
                <>
                    {' '}
                    <Translation>
                        {(t) =>
                            t(
                                'filters:DateRangeFilter.weekCount',
                                '({{count}} week)',
                                { count: weeks }
                            )
                        }
                    </Translation>
                </>
            ) : (
                ''
            )}
        </>
    )
}

export const calculateSegmentLag = (): number => {
    const today = date().utc().startOf('day')
    const nbOfDaysSinceLastSaturday = (today.day() + 1) % 7

    return nbOfDaysSinceLastSaturday * 24
}

export const calculateCategoryPerformanceLag = (): number =>
    date().diff(date().startOf('week').subtract(3, 'weeks'), 'hours')

/**
 * The end of the most recent week whose data has fully landed: weekly data
 * arrives on a ~2 week lag, so it is the Saturday two weeks back. Market Analysis
 * anchors on the same date (`rocket/src/lib/salesDataBoundary.ts`), which is what
 * makes the two products agree on what "Last Month" covers.
 *
 * A calendar date rather than an hour count, because an hour count cannot express
 * "the Saturday two weeks back" — it only holds while the value is re-read and
 * the arithmetic stays in one zone. The `date` factory constructs in UTC app-wide, so
 * both hold today; anchoring on the date means neither is load-bearing.
 */
export function getWeeklyDataBoundary(today: Dayjs = date()): string {
    const anchor = today.utc().startOf('day')
    const daysSinceSaturday = (anchor.day() + 1) % 7

    return anchor.subtract(daysSinceSaturday + 7, 'days').format('YYYY-MM-DD')
}

/**
 * The boundary expressed as the lag `presetRanges` consumes.
 *
 * Measured in whole calendar days, which callers depend on: a continuously
 * varying lag would leave the date picker's Apply button permanently enabled and
 * re-resolve every tab's presets on every render — and since a tab takes the
 * largest lag across its data sources, that damage would not stay inside the
 * weekly one.
 *
 * Both the boundary and today are UTC dates: the boundary names the same
 * Saturday Market Analysis names, and `presetRanges` spends this value by
 * subtracting it from a UTC `date()`, so measuring in the same zone keeps the
 * anchor on that Saturday.
 */
export function hoursToWeeklyDataBoundary(): number {
    // One clock read, shared by both halves. Two reads either side of the UTC day
    // boundary disagree by a day, which is enough to move a week-anchored preset.
    const now = date()

    return (
        now.startOf('day').diff(date(getWeeklyDataBoundary(now)), 'days') * 24
    )
}

/**
 * Hold a range's end at the last fully landed week.
 *
 * A range sitting entirely past the boundary is returned untouched rather than
 * inverted: it holds no data either way, and an inverted range is a request
 * error instead of an empty widget.
 */
export function clampEndToWeeklyDataBoundary<T extends string | undefined>(
    start: string | undefined,
    end: T,
    boundary: string = getWeeklyDataBoundary()
): T | string {
    if (!end || end <= boundary || (start && start > boundary)) {
        return end
    }
    return boundary
}

/**
 * Widen a range **outward** to whole Sun–Sat weeks, then clamp the end to the
 * data boundary. Matches what hulk's `align_*_to_grain` does server-side, so the
 * picker and the export's declared range agree with the data that comes back.
 *
 * Note the direction: `alignToFullWeeksWithLag` snaps *inward* and has other
 * callers — the two are not interchangeable.
 */
export function snapToFullWeeks(
    start: Dayjs,
    end: Dayjs,
    boundary?: string
): [Dayjs, Dayjs] {
    const snappedStart = date(start).startOf('day').day(0)
    const snappedEnd = date(end).startOf('day').day(6)
    const clamped = clampEndToWeeklyDataBoundary(
        snappedStart.format('YYYY-MM-DD'),
        snappedEnd.format('YYYY-MM-DD'),
        boundary
    )

    return [snappedStart, date(clamped)]
}

/**
 * Align a [start, end] date range to full Sun–Sat weeks and cap by lag date
 * Returns formatted YYYY-MM-DD strings
 */
export function alignToFullWeeksWithLag(
    startISO: string,
    endISO: string,
    lagHours: number = 0
): { start?: string; end?: string } {
    if (lagHours <= 0) {
        return { start: startISO, end: endISO }
    }

    const lagDate = date().utc().subtract(lagHours, 'hours').startOf('day')
    let startDate = date(startISO)
    let endDate = date(endISO)

    if (startDate.day() !== 0) {
        startDate = startDate.subtract(startDate.day(), 'days')
    }
    if (endDate.day() !== 6) {
        endDate = endDate.subtract((endDate.day() + 1) % 7, 'days')
    }
    if (endDate.isAfter(lagDate)) {
        endDate = lagDate
    }

    return {
        start: startDate.format('YYYY-MM-DD'),
        end: endDate.format('YYYY-MM-DD'),
    }
}

/**
 * Segments are refreshed every Monday and use data through the
 * previous Saturday. If this schedule changes, this function will
 * need to be updated to reflect the new schedule.
 */
export const calculateSegmentLagUsingLastRefresh = (
    last_refresh_at?: string
): number => {
    const today = date().utc().startOf('day')
    const lastRefresh = date(last_refresh_at).utc().startOf('day')
    const daysSinceLastRefresh = today.diff(lastRefresh, 'days')
    let daysSinceLastSaturday = (lastRefresh.day() + 1) % 7
    if (daysSinceLastSaturday <= 1) {
        // if today is Saturday or Sunday, go back to previous Saturday to accomodate lag
        daysSinceLastSaturday += 7
    }

    return (daysSinceLastRefresh + daysSinceLastSaturday) * 24
}

export function getPreviousPeriodDateRangeDisplayText(
    dateRange: string | undefined,
    t: any
): string {
    if (!dateRange) {
        return t('filters:DateRangeFilter.previousPeriod', 'Previous Period')
    }

    switch (dateRange as PresetRange) {
        case 'last_30_days':
            return t(
                'filters:DateRangeFilter.presetRanges.last30Days',
                'Last 30 Days'
            )
        case 'last_month':
            return t(
                'filters:DateRangeFilter.presetRanges.lastMonth',
                'Last Month'
            )
        case 'last_60_days':
            return t(
                'filters:DateRangeFilter.presetRanges.last60Days',
                'Last 60 Days'
            )
        case 'this_quarter':
            return t(
                'filters:DateRangeFilter.presetRanges.lastQuarter',
                'Last Quarter'
            )
        case 'last_90_days':
            return t(
                'filters:DateRangeFilter.presetRanges.last90Days',
                'Last 90 Days'
            )
        case 'last_3_months':
            return t(
                'filters:DateRangeFilter.presetRanges.last3Months',
                'Last 3 Months'
            )
        case 'last_quarter':
        case 'quarter_to_date':
            return t(
                'filters:DateRangeFilter.presetRanges.lastQuarter',
                'Last Quarter'
            )
        case 'last_6_months':
            return t(
                'filters:DateRangeFilter.presetRanges.last6Months',
                'Last 6 Months'
            )
        case 'this_year':
            return t(
                'filters:DateRangeFilter.presetRanges.lastYear',
                'Last year'
            )
        case 'last_12_months':
            return t(
                'filters:DateRangeFilter.presetRanges.last12Months',
                'Last 12 Months'
            )
        case 'year_to_date':
            return t(
                'filters:DateRangeFilter.presetRanges.yearToDate',
                'Year-to-Date'
            )
        case 'fiscal_year_to_date':
            return t(
                'filters:DateRangeFilter.presetRanges.fiscalYearToDate',
                'Fiscal Year to Date'
            )
        case 'last_fiscal_year':
            return t(
                'filters:DateRangeFilter.presetRanges.lastFiscalYear',
                'Last Fiscal Year'
            )
        default:
            return t(
                'filters:DateRangeFilter.previousPeriod',
                'Previous Period'
            )
    }
}

export const getPeriodDeltaNames = (
    t: TFunction
): Record<PeriodDeltaType, { label: string }> => ({
    prior_period: { label: t('common:priorPeriod', 'Prior Period') },
    prior_year: { label: t('common:yearOverYear', 'Year-over-Year') },
    custom: { label: t('common:custom', 'Custom') },
})

export function alignDateRangeToAggregation(
    rangeStart: Dayjs,
    rangeEnd: Dayjs,
    aggregation?: Aggregation
): [Dayjs, Dayjs] {
    if (!aggregation || aggregation === 'day') {
        return [rangeStart, rangeEnd]
    }

    if (aggregation !== 'week' && aggregation !== 'month') {
        // Preserve the original range semantics (e.g. endOf('day') from presetRanges)
        // for aggregations this helper does not align.
        return [rangeStart, rangeEnd]
    }

    const start = rangeStart.startOf('day')
    const end = rangeEnd.startOf('day')

    if (aggregation === 'week') {
        const alignedStart =
            start.day() === 0
                ? start
                : start.endOf('week').add(1, 'day').startOf('day')
        const alignedEnd = end.endOf('week')
        return [alignedStart, alignedEnd]
    }

    const alignedStart =
        start.date() === 1
            ? start
            : start.endOf('month').add(1, 'day').startOf('day')
    const alignedEnd = end.endOf('month')
    return [alignedStart, alignedEnd]
}

export function getRangeWithAggregationAndLag(
    value: PresetRange,
    lagHours: number,
    aggregation?: Aggregation,
    fiscalYearStartMonth?: number
): [Dayjs, Dayjs] {
    const { range } = presetRanges(date(), lagHours, fiscalYearStartMonth)[
        value
    ]

    // Only week/month are aligned; day/year/other keep the raw preset range
    // (including endOf('day') from presetRanges).
    if (aggregation === 'week' || aggregation === 'month') {
        return alignDateRangeToAggregation(range[0], range[1], aggregation)
    }

    return range
}
